/* eslint-disable react/jsx-no-useless-fragment */
import { useAgents } from '#~/agent/hooks.js';
import { fieldKeyedAgentsEqual, idNamePick } from '#~/agent/provider/use-agents-field-eq.js';
import { Sel, STag } from '#~/stag/tag.js';
import { Button } from '@studio/design-system/button';
import { Spinner } from '@studio/design-system/spinner';
import { pick } from 'lodash-es';
import plur from 'plur';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { SummaryProps } from './types.js';

/** `<Summary>` component. */
export const Summary = ({
  className,
  count,
  discovery,
  qualifier,
  ...props
}: SummaryProps): ReactElement => {
  // Count every agent, including embedded — a studio with only Iris-firmware
  // cameras is still a live studio.
  const agents = useAgents(
    (µ) => µ.map((a) => pick(a, ['id', 'name'])),
    (a, b) => fieldKeyedAgentsEqual(a, b, idNamePick)
  );

  if (agents.length === 0) {
    return <></>;
  }

  const { discover, isRunning, isActive, failureCopy } = discovery;

  const q = qualifier?.length ? `${qualifier} ` : '';

  const countText = `${count} ${q}${plur('device', count)} found.`;
  // A failed Bridge must not erase successful results or look like an empty scan.
  const statusText = failureCopy
    ? count > 0
      ? `${countText} ${failureCopy}`
      : failureCopy
    : isRunning
      ? 'Searching for devices...'
      : isActive && count === 0
        ? 'Scanning for devices...'
        : count === 0
          ? `No ${q}devices found.`
          : countText;

  return (
    <div className={twMerge('flex items-center justify-between p-16', className)} {...props}>
      <>
        <div className='text-sm font-demibold'>{statusText}</div>

        <div>
          {isRunning ? (
            <Spinner className='h-24 w-24' />
          ) : (
            <Button
              onClick={discover}
              size='sm'
              variant='secondary'
              {...STag(Sel.AddVideoDeviceDiscovery, 'search')}
            >
              Search Again
            </Button>
          )}
        </div>
      </>
    </div>
  );
};
